import React, { useState, useEffect, useRef } from "react";
import { createGroup, updateGroup, searchVocabularies } from "./adminSimilarWordsService";
import ImagePickerModal from "../../media_images/ImagePickerModal";
import "../../styles/content.css";

const ROOT = process.env.REACT_APP_BASE_URL_ROOT;
const toPublicImgSrc = (url) => {
  if (!url) return "";
  const pub = url.replace(/\/api(?:\/admin)?\/media-images\//, "/api/public/media-images/");
  const src = pub.startsWith("http") ? pub : `${ROOT}${pub}`;
  console.log("[WordGroupForm] img raw:", url, "→ resolved:", src);
  return src;
};

const WordGroupForm = ({ group, onSaved, onCancel }) => {
  const isEdit = !!group;

  const [form, setForm] = useState({ title: "", youtubeVideoLink: "", imageUrl: "", isActive: true, displayOrder: 1 });
  const [loading, setLoading] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);

  const [vocabSearch, setVocabSearch] = useState("");
  const [vocabResults, setVocabResults] = useState([]);
  const [vocabSearching, setVocabSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedVocabs, setSelectedVocabs] = useState([]);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (group) {
      setForm({
        title: group.title || "",
        youtubeVideoLink: group.youtubeVideoLink || "",
        imageUrl: group.imageUrl || "",
        isActive: group.isActive !== false,
        displayOrder: group.displayOrder || 1,
      });
    }
  }, [group]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleVocabSearchChange = (e) => {
    const val = e.target.value;
    setVocabSearch(val);
    setShowDropdown(true);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setVocabResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      console.log("[WordGroupForm] searching vocab:", val.trim());
      setVocabSearching(true);
      const res = await searchVocabularies(val.trim());
      console.log("[WordGroupForm] vocab search result:", res.status, res.data?.content?.length, "items");
      if (res.status === 200) setVocabResults(res.data?.content || []);
      else setVocabResults([]);
      setVocabSearching(false);
    }, 300);
  };

  const handleVocabSelect = (vocab) => {
    console.log("[WordGroupForm] vocab selected:", vocab.id, vocab.word);
    if (!selectedVocabs.find((v) => v.id === vocab.id)) {
      setSelectedVocabs((prev) => [...prev, vocab]);
    }
    setVocabSearch("");
    setVocabResults([]);
    setShowDropdown(false);
  };

  const handleVocabRemove = (id) => {
    console.log("[WordGroupForm] vocab removed:", id);
    setSelectedVocabs((prev) => prev.filter((v) => v.id !== id));
  };

  const handleSubmit = async () => {
    if (!form.title) { alert("Title is required"); return; }
    const payload = {
      title: form.title,
      youtubeVideoLink: form.youtubeVideoLink || null,
      imageUrl: form.imageUrl || null,
      isActive: form.isActive,
      displayOrder: Number(form.displayOrder) || 1,
      vocabularyIds: selectedVocabs.length > 0 ? selectedVocabs.map((v) => v.id) : null,
    };
    console.log("[WordGroupForm] submit — isEdit:", isEdit, "| payload:", JSON.stringify(payload, null, 2));
    setLoading(true);
    const res = isEdit ? await updateGroup(group.id, payload) : await createGroup(payload);
    console.log("[WordGroupForm] submit result:", res.status, JSON.stringify(res.data || res.message, null, 2));
    if (res.status === 200 || res.status === 201) { onSaved(res.data?.data || res.data); }
    else { alert(res.message || "Operation failed"); }
    setLoading(false);
  };

  const filteredResults = vocabResults.filter((v) => !selectedVocabs.find((s) => s.id === v.id));

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: 520, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <h2>{isEdit ? "Edit" : "Create"} Word Group</h2>

          <div className="form-group"><label>Title *</label><input name="title" placeholder="e.g. Break, Brake" value={form.title} onChange={handleChange} /></div>
          <div className="form-group"><label>YouTube Video Link</label><input name="youtubeVideoLink" placeholder="https://youtube.com/watch?v=..." value={form.youtubeVideoLink} onChange={handleChange} /></div>

          <div className="form-group">
            <label>Image (optional)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {form.imageUrl ? (
                <img src={toPublicImgSrc(form.imageUrl)} alt="selected"
                  style={{ width: 60, height: 60, objectFit: "contain", background: "#f5f5f5", borderRadius: 8, border: "1px solid #ddd" }} />
              ) : (
                <div style={{ width: 60, height: 60, background: "#f0f0f0", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#aaa" }}>No image</div>
              )}
              <button type="button" className="btn secondary" onClick={() => setShowImagePicker(true)}>Pick Image</button>
              {form.imageUrl && <button type="button" className="btn secondary" onClick={() => setForm({ ...form, imageUrl: "" })}>Clear</button>}
            </div>
          </div>

          {/* Vocabularies selector */}
          <div className="form-group">
            <label>Add words from Vocabulary (optional)</label>

            {selectedVocabs.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {selectedVocabs.map((v) => (
                  <span key={v.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#dbeafe", color: "#1e40af", padding: "3px 10px", borderRadius: 20, fontSize: 13, fontWeight: 500 }}>
                    {v.word}
                    <button type="button" onClick={() => handleVocabRemove(v.id)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#1e40af", fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}

            <div style={{ position: "relative" }} ref={dropdownRef}>
              <input
                type="text"
                placeholder="Search and select vocabularies..."
                value={vocabSearch}
                onChange={handleVocabSearchChange}
                onFocus={() => vocabResults.length > 0 && setShowDropdown(true)}
                autoComplete="off"
              />
              {showDropdown && (
                <div style={{ position: "absolute", zIndex: 20, width: "100%", background: "#fff", border: "1px solid #ddd", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", maxHeight: 220, overflowY: "auto", marginTop: 4 }}>
                  {vocabSearching ? (
                    <div style={{ padding: 12, fontSize: 13, color: "#888", textAlign: "center" }}>Loading...</div>
                  ) : filteredResults.length === 0 ? (
                    <div style={{ padding: 12, fontSize: 13, color: "#888", textAlign: "center" }}>
                      {vocabSearch.length < 2 ? "Start typing to search vocabularies..." : `No results for "${vocabSearch}"`}
                    </div>
                  ) : (
                    filteredResults.map((v) => (
                      <button key={v.id} type="button" onClick={() => handleVocabSelect(v)}
                        style={{ width: "100%", textAlign: "left", padding: "9px 14px", background: "none", border: "none", borderBottom: "1px solid #f0f0f0", cursor: "pointer", fontSize: 13 }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fa"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "none"}>
                        <div style={{ fontWeight: 600 }}>{v.word}</div>
                        {v.definition && <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{v.definition.length > 60 ? v.definition.slice(0, 60) + "…" : v.definition}</div>}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-group"><label>Display Order</label><input name="displayOrder" type="number" min="1" value={form.displayOrder} onChange={handleChange} /></div>
          <div className="form-group"><label style={{ display: "flex", alignItems: "center", gap: 8 }}><input name="isActive" type="checkbox" checked={form.isActive} onChange={handleChange} /> Active</label></div>

          <div className="form-actions">
            <button onClick={handleSubmit} disabled={loading}>{loading ? "Saving..." : isEdit ? "Update" : "Create"}</button>
            <button onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>

      <ImagePickerModal
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        onSelect={(url) => { setForm({ ...form, imageUrl: url }); setShowImagePicker(false); }}
        defaultImageUse="GENERAL"
      />
    </div>
  );


};

export default WordGroupForm;
